import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../lib/auth";
import CpvCombobox from "../components/CpvCombobox";

const CERTS = ["Cyber Essentials", "Cyber Essentials Plus", "ISO 9001", "ISO 27001", "ISO 14001", "JOSCAR", "Hellios"];
const CLEARS = ["BPSS", "SC", "DV"];
const TURNS = ["<250k", "250k-1m", "1m-5m", "5m-10m", "10m-25m", "25m-50m", ">50m", "unknown"];

export default function Profile() {
  const { activeCompany } = useAuth();
  const [profile, setProfile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get("/profile").then((r) => setProfile(r.data)).catch(() => {});
  }, [activeCompany?.id]);

  if (!profile) return <span className="spinner" />;

  const toggle = (k, v) => {
    setProfile({ ...profile, [k]: (profile[k] || []).includes(v)
      ? profile[k].filter((x) => x !== v) : [...(profile[k] || []), v] });
  };

  const save = async () => {
    setSaving(true); setError(""); setSaved(false);
    try {
      const { data } = await api.put("/profile", profile);
      setProfile(data);
      setSaved(true);
      setTimeout(() => setSaved(false), 1500);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setSaving(false); }
  };

  return (
    <div data-testid="profile-page">
      <div className="flex justify-between items-end mb-6">
        <div>
          <div className="label-meta">Account</div>
          <div className="display">Company profile</div>
        </div>
        <div className="flex items-center gap-3">
          {saved && <span className="text-[12px]" style={{ color: "var(--parapet-text-secondary)" }}>Saved ✓</span>}
          <button onClick={save} className="btn-primary" disabled={saving} data-testid="profile-save-button">
            {saving ? "Saving…" : "Save changes"}
          </button>
        </div>
      </div>
      {error && <div className="text-[12px] p-2 border mb-4" style={{ borderColor: "#111" }}>{error}</div>}

      <div className="grid grid-cols-3 gap-4">
        <div className="p-card col-span-2 space-y-4">
          <div>
            <label className="p-label">Capability statement</label>
            <textarea className="p-input" rows={6} value={profile.capability_statement || ""}
                      onChange={(e) => setProfile({ ...profile, capability_statement: e.target.value })}
                      data-testid="profile-capability-input" />
          </div>
          <div>
            <label className="p-label">CPV codes</label>
            <CpvCombobox
              mode="multi"
              value={profile.cpv_codes || []}
              onChange={(arr) => setProfile({ ...profile, cpv_codes: arr })}
              placeholder="Search defence CPV codes (e.g. radar, 35000…)"
              data-testid="profile-cpv-combobox"
            />
          </div>
          <div>
            <label className="p-label">Certifications</label>
            <div className="flex gap-2 flex-wrap">
              {CERTS.map((c) => (
                <button key={c} className={"filter-chip" + (profile.certifications?.includes(c) ? " active" : "")}
                        onClick={() => toggle("certifications", c)} type="button">{c}</button>
              ))}
            </div>
          </div>
          <div>
            <label className="p-label">Security clearances</label>
            <div className="flex gap-2 flex-wrap">
              {CLEARS.map((c) => (
                <button key={c} className={"filter-chip" + (profile.security_clearances?.includes(c) ? " active" : "")}
                        onClick={() => toggle("security_clearances", c)} type="button">{c}</button>
              ))}
            </div>
          </div>
        </div>

        <div className="p-card space-y-4">
          <div>
            <label className="p-label">Turnover band</label>
            <select className="p-input" value={profile.turnover_band || "unknown"}
                    onChange={(e) => setProfile({ ...profile, turnover_band: e.target.value })}
                    data-testid="profile-turnover-select">
              {TURNS.map((b) => <option key={b} value={b}>{b}</option>)}
            </select>
          </div>
          <div>
            <label className="p-label">JOSCAR status</label>
            <select className="p-input" value={profile.joscar_status || "none"}
                    onChange={(e) => setProfile({ ...profile, joscar_status: e.target.value })}>
              <option value="none">Not registered</option><option value="in_progress">In progress</option><option value="registered">Registered</option>
            </select>
          </div>
          <div>
            <label className="p-label">Hellios status</label>
            <select className="p-input" value={profile.hellios_status || "none"}
                    onChange={(e) => setProfile({ ...profile, hellios_status: e.target.value })}>
              <option value="none">Not registered</option><option value="in_progress">In progress</option><option value="registered">Registered</option>
            </select>
          </div>
          <div>
            <label className="p-label">Alert threshold (≥)</label>
            <input type="range" min="0" max="100" value={profile.alert_threshold ?? 60}
                   onChange={(e) => setProfile({ ...profile, alert_threshold: parseInt(e.target.value) })}
                   className="w-full" data-testid="profile-alert-threshold" />
            <div className="text-[12px] text-right value-mono">{profile.alert_threshold ?? 60}</div>
          </div>
        </div>
      </div>
    </div>
  );
}
